<template>
  <div v-if="isVisibleOnCurrentPage" class="p-4 glass-frosted h-full flex flex-col rounded-8xl">
    <div
      class="w-full flex items-start justify-between mb-3"
      :class="size === 'compact' ? 'relative' : 'static'"
    >
      <p class="text-black-white text-l-medium">{{ $t('parameters.keyDebounce.title') }}</p>
      <div
        class="flex items-center gap-2"
        :class="size === 'compact' ? 'absolute top-12 left-0' : 'static'"
      >
        <Button
          color="glass"
          size="sm"
          :active="activeTab === 'press'"
          class="h-7.5"
          @click="switchTab('press')"
        >
          {{ $t('parameters.keyDebounce.press') }}
        </Button>
        <Button
          color="glass"
          size="sm"
          :active="activeTab === 'release'"
          class="h-7.5"
          @click="switchTab('release')"
        >
          {{ $t('parameters.keyDebounce.release') }}
        </Button>
      </div>
    </div>
    <div class="flex-1 flex items-center">
      <div class="w-full">
        <Slider
          v-model="currentValue"
          :min="0"
          :max="100"
          :step="0.01"
          thumb-alignment="contain"
          range-alignment="thumb-end"
          range-edge-behavior="aligned"
          :thumb-size="THUMB_WIDTH"
          class="h-8 w-full"
          :range-class="['bg-white', positionTransitionClass]"
          track-class="bg-glass30 h-8"
          :thumb-class="['bg-transparent rounded-none', positionTransitionClass]"
          @pointerdown.capture="hasInteracted = true"
          @update:model-value="onSliderDrag"
          @value-commit="onSliderCommit"
        >
          <template #thumb>
            <div class="relative flex items-center justify-center w-full h-full">
              <Icon icon="Direction-FIll-SwipeLR1" :class="'size-8'" />
              <div
                class="absolute -z-1 w-px bg-black-white left-[50%] -top-1 -translate-x-1/2"
                :class="'h-4'"
              />
              <div
                class="absolute -z-1 w-px bg-black-white left-[50%] -bottom-1 -translate-x-1/2"
                :class="'h-4'"
              />
            </div>
          </template>
        </Slider>
        <!-- 刻度 -->
        <div class="relative mt-2 h-4">
          <span
            v-for="tick in tickMarks"
            :key="tick.value"
            class="absolute -translate-x-1/2 cursor-pointer select-none"
            :class="
              highlightValue === tick.value
                ? 'text-brand-blue1 text-l-semibold'
                : 'text-text-glass-default text-s-medium'
            "
            :style="{
              left: `calc(${THUMB_HALF}px + ${tick.percent / 100} * (100% - ${THUMB_WIDTH}px))`,
            }"
            @click="jumpToTick(tick.value)"
          >
            {{ tick.value }}
          </span>
          <Transition
            mode="out-in"
            enter-active-class="animate-in fade-in duration-200"
            leave-active-class="animate-out fade-out duration-200"
          >
            <div
              v-if="hasLowDelayRisk"
              class="absolute top-8 p-1 pt-0.5 w-fit bg-state-warning rounded-m"
            >
              <span class="text-s-medium text-white-black">
                {{ $t('parameters.keyDebounce.tip') }}
              </span>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useRoute } from 'vue-router'
  import { Button, Icon, Slider } from '@rapoo/element'
  import type { KeyDebounceDisplayPage, ParametersCapability } from '@rapoo/device-sdk'
  import { useDeviceStore } from '@/stores'
  import type { ModuleSize } from '../utils/types'
  import type { ParametersContext } from '../../composables/useParameters'
  import { useLatestCommit } from '@/composables/useSliderDrag'

  /** thumb 尺寸：由 Slider 同时用于视觉尺寸、range 对齐和刻度定位。 */
  const THUMB_WIDTH = 32
  const THUMB_HALF = THUMB_WIDTH / 2
  const MAX_VALUE = 32

  const props = defineProps<{
    parameters: ParametersContext
    size?: ModuleSize
  }>()

  const ctx = props.parameters
  const route = useRoute()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const configuredDisplayPage = computed<KeyDebounceDisplayPage>(() => {
    const capability = currentDevice.value?.manifest.capabilities.find(
      (item): item is ParametersCapability => item.type === 'parameters',
    )
    return capability?.config.keyDebounce?.displayPage ?? ''
  })

  const currentDisplayPage = computed<KeyDebounceDisplayPage | null>(() => {
    if (route.name === 'mouse-rt') return 'rt'
    if (route.name === 'mouse-parameters') return ''
    return null
  })

  /** 未配置/空值沿用参数页；配置 rt 后仅在按键行程页展示。 */
  const isVisibleOnCurrentPage = computed(
    () => configuredDisplayPage.value === currentDisplayPage.value,
  )

  const activeTab = computed(() => ctx.state.keyDebounce.value.activeTab)
  const pressValue = computed(() => ctx.state.keyDebounce.value.pressValue)
  const releaseValue = computed(() => ctx.state.keyDebounce.value.releaseValue)

  const hasLowDelayRisk = computed(
    () =>
      (activeTab.value === 'press' && pressValue.value === 0) ||
      (activeTab.value === 'release' && releaseValue.value === 0),
  )

  // 有效的刻度值：0-16 step 1，16-32 step 2
  const validSteps = computed(() => {
    const steps: number[] = []
    for (let i = 0; i <= 16; i++) steps.push(i)
    for (let i = 18; i <= 32; i += 2) steps.push(i)
    return steps
  })

  // 刻度标记及其百分比位置
  const tickMarks = computed(() => {
    return validSteps.value.map((v) => ({
      value: v,
      percent: (v / MAX_VALUE) * 100,
    }))
  })

  // 拖动过程中的临时高亮值
  const draggingValue = ref<number | null>(null)
  const pendingCommit = ref<{ id: number; tab: 'press' | 'release'; value: number } | null>(null)
  let commitId = 0

  // 拖动和设备写入期间都保持用户刚选择的刻度，避免先闪回旧值再显示新值。
  const highlightValue = computed(() => {
    if (draggingValue.value !== null) return draggingValue.value
    if (pendingCommit.value?.tab === activeTab.value) return pendingCommit.value.value
    return activeTab.value === 'press' ? pressValue.value : releaseValue.value
  })

  // slider 内部值为百分比 0-100
  const currentValue = ref([0])
  const hasInteracted = ref(false)
  const positionTransitionClass = computed(() =>
    hasInteracted.value && !ctx.state.loading.value
      ? 'transition-[left,right,top,bottom] duration-150 ease-out motion-reduce:transition-none'
      : '',
  )

  // 当外部状态变化时同步 slider
  watch(
    [activeTab, pressValue, releaseValue],
    () => {
      // 写入尚未完成时，设备侧的旧值或前一次写入结果不能覆盖当前预览。
      if (pendingCommit.value?.tab === activeTab.value) return
      draggingValue.value = null
      const val = activeTab.value === 'press' ? pressValue.value : releaseValue.value
      currentValue.value = [valueToPercent(val)]
    },
    { immediate: true },
  )

  // 切换 tab
  function switchTab(tab: 'press' | 'release') {
    hasInteracted.value = true
    ctx.actions.setKeyDebounceTab(tab)
  }

  // 将实际值转为 slider 百分比
  function valueToPercent(val: number): number {
    return (val / MAX_VALUE) * 100
  }

  // 将 slider 百分比转为实际值，吸附到最近的有效刻度
  function percentToSnappedValue(percent: number): number {
    const rawValue = (percent / 100) * MAX_VALUE
    let closest = validSteps.value[0]
    let minDist = Math.abs(rawValue - closest)
    for (const step of validSteps.value) {
      const dist = Math.abs(rawValue - step)
      if (dist < minDist) {
        minDist = dist
        closest = step
      }
    }
    return closest
  }

  function onSliderDrag(val: number[] | undefined) {
    if (!val) return
    const snapped = percentToSnappedValue(val[0])
    draggingValue.value = snapped
    currentValue.value = [valueToPercent(snapped)]
  }

  const { commit: commitLatestDebounce } = useLatestCommit(
    async (pending: { id: number; tab: 'press' | 'release'; value: number }) => {
      try {
        if (pending.tab === 'press') {
          await ctx.actions.setKeyDebouncePressValue(pending.value)
        } else {
          await ctx.actions.setKeyDebounceReleaseValue(pending.value)
        }
      } catch {
        // 写入失败已由 composable 统一上报；恢复当前设备确认的值。
        if (pendingCommit.value?.id === pending.id) {
          const current = pending.tab === 'press' ? pressValue.value : releaseValue.value
          currentValue.value = [valueToPercent(current)]
        }
      } finally {
        // 旧请求完成时不能清除后一次操作的临时展示。
        if (pendingCommit.value?.id === pending.id) {
          pendingCommit.value = null
          draggingValue.value = null
        }
      }
    },
  )

  function commitValue(value: number) {
    hasInteracted.value = true
    const tab = activeTab.value
    const pending = { id: ++commitId, tab, value }
    pendingCommit.value = pending
    draggingValue.value = value
    currentValue.value = [valueToPercent(value)]
    commitLatestDebounce(pending)
  }

  // 松开或点击 track 时：真正提交值
  function onSliderCommit(val: number[]) {
    commitValue(percentToSnappedValue(val[0]))
  }

  // 点击刻度跳转：直接提交
  function jumpToTick(value: number) {
    commitValue(value)
  }
</script>
